import { useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Bell, LogOut, Settings } from 'lucide-react';
import { Button, Dialog, DialogContent, cn } from '@nw/ui';
import { api, toast, useFormat, useIntegrations, useMe, usePreferences, useUpdatePreferences } from '@nw/web-platform';
import { useWallpaper } from './wallpaper.js';

export function useNotifications(pending: number) {
  const diag = useQuery({
    queryKey: ['diagnostics'],
    queryFn: async () =>
      (
        await api<{
          outbox: { failed: number; blocked: number };
          failedEvents: Array<{
            id: string;
            type: string;
            lastError: string | null;
          }>;
        }>('/diagnostics')
      ).data,
    refetchInterval: 30_000,
  });
  const integrations = useIntegrations();
  const fmt = useFormat();
  const items: Array<{
    id: string;
    title: string;
    body: string;
    href?: string;
  }> = [];
  if (pending > 0)
    items.push({
      id: 'pending',
      title: 'Updating connections',
      body: `${pending} recent change${pending === 1 ? '' : 's'} are being added to search and the network.`,
    });
  for (const e of diag.data?.failedEvents ?? [])
    items.push({
      id: e.id,
      title: 'A connection update failed',
      body: `${e.type}: ${e.lastError ?? 'unknown error'}. Retry it in Settings.`,
      href: '/settings',
    });
  if (diag.data?.outbox.blocked)
    items.push({
      id: 'blocked',
      title: 'Updates paused',
      body: `${diag.data.outbox.blocked} updates wait for a module that is turned off.`,
      href: '/settings',
    });
  for (const i of integrations.data ?? []) {
    if (i.lastErrorCode)
      items.push({
        id: `int-${i.providerId}`,
        title: `${i.name} had a problem`,
        body: `${i.lastErrorCode.replace(/_/g, ' ')}${i.lastErrorAt ? `, ${fmt.relative(i.lastErrorAt)}` : ''}.`,
        href: '/settings',
      });
  }
  return items;
}

const choice = (on: boolean) => cn('nw-choice', on && 'nw-choice-on');

/** You (top right): notices, atmosphere, Home's backdrop, settings and sign out, in one sheet. */
export function YouSheet({ open, onOpenChange, pending, onSignOut }: { open: boolean; onOpenChange: (o: boolean) => void; pending: number; onSignOut: () => void }) {
  const me = useMe();
  const prefs = usePreferences();
  const update = useUpdatePreferences();
  const notices = useNotifications(pending);
  const atmosphere = prefs.data?.atmosphere ?? 'auto';
  const wallpaper = useWallpaper();
  const fileRef = useRef<HTMLInputElement>(null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={me.data?.user.displayName ?? 'You'} description={me.data?.workspace.name}>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          aria-hidden
          tabIndex={-1}
          onChange={async (e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (!f) return;
            if (await wallpaper.setPhoto(f)) toast.success('Wallpaper set on this device');
            else toast.error('That image could not be used. Try a JPEG or PNG under 20 MB.');
          }}
        />
        <section aria-labelledby="you-notices">
          <h3 id="you-notices" className="nw-label mb-2 flex items-center gap-2">
            <Bell className="size-3.5" /> Notices
          </h3>
          {notices.length ? (
            <ul className="grid gap-1">
              {notices.map((n) => (
                <li key={n.id} className="rounded-[14px] bg-[var(--nw-surface-sunk)] px-3 py-2.5">
                  {n.href ? (
                    <Link to={n.href} className="block" onClick={() => onOpenChange(false)}>
                      <span className="block text-sm font-semibold">{n.title}</span>
                      <span className="block text-xs text-[var(--nw-quiet)]">{n.body}</span>
                    </Link>
                  ) : (
                    <>
                      <span className="block text-sm font-semibold">{n.title}</span>
                      <span className="block text-xs text-[var(--nw-quiet)]">{n.body}</span>
                    </>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[var(--nw-quiet)]">All quiet. Nothing needs your attention.</p>
          )}
        </section>
        <section aria-labelledby="you-atmos" className="mt-6">
          <h3 id="you-atmos" className="nw-label mb-2">
            Atmosphere
          </h3>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby="you-atmos">
            {(
              [
                ['auto', 'Follow the day'],
                ['aurora', 'Dawn'],
                ['fog', 'Mist'],
                ['deep', 'Night'],
              ] as const
            ).map(([v, l]) => (
              <button key={v} type="button" role="radio" aria-checked={atmosphere === v} className={choice(atmosphere === v)} onClick={() => update.mutate({ atmosphere: v })}>
                {l}
              </button>
            ))}
          </div>
        </section>
        <section aria-labelledby="you-wall" className="mt-6">
          <h3 id="you-wall" className="nw-label mb-2">
            Home backdrop on this device
          </h3>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby="you-wall">
            <button type="button" role="radio" aria-checked={wallpaper.choice === 'scene'} className={choice(wallpaper.choice === 'scene')} onClick={() => wallpaper.choose('scene')}>
              Living scene
            </button>
            <button type="button" role="radio" aria-checked={wallpaper.choice === 'photo'} className={choice(wallpaper.choice === 'photo')} onClick={() => fileRef.current?.click()}>
              {wallpaper.photo ? 'Your photo (change…)' : 'Choose a photo…'}
            </button>
            {wallpaper.photo && wallpaper.choice !== 'photo' ? (
              <button type="button" className={choice(false)} onClick={() => wallpaper.choose('photo')}>
                Use your photo
              </button>
            ) : null}
          </div>
        </section>
        <div className="mt-8 flex flex-wrap gap-2">
          <Button asChild variant="secondary">
            <Link to="/settings" onClick={() => onOpenChange(false)}>
              <Settings /> Settings
            </Link>
          </Button>
          <Button variant="quiet" onClick={onSignOut}>
            <LogOut /> Sign out
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
